import { useState } from 'react'
import { View, Text, Pressable, Modal, ScrollView, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'

// Selettore gara della pagina Pick (stessa idea del DaySwitcher di VB): poche pillole affiancate, tutte
// visibili, ognuna con il nome e lo stato scritto a parole (PICK / LIVE / CONCLUSA). Niente scorrimento
// ne' chip con numeri: le gare attive stanno in linea, le altre (passate o ancora lontane) nello sheet "storico".
//
// races: [{ value, label, type, status: 'pick' | 'live' | 'done', date }] gia' in ordine di data.
// pills: gare da mostrare in linea. more: gare non in linea (prossime lontane + passate) per lo sheet.
export default function RaceSwitcher({ pills, nextMore, past, selected, onSelect, labels }) {
  const { t, i18n } = useTranslation()
  const tc = useThemeColors()
  const insets = useSafeAreaInsets()
  const [open, setOpen] = useState(false)

  // labels: { live, done, pick } per cambiare le diciture (la classifica chiama "in arrivo" la gara senza risultati)
  const tagOf = (status) => status === 'live' ? (labels?.live ?? t('pick.race_tag_live')) : status === 'done' ? (labels?.done ?? t('pick.race_tag_done')) : (labels?.pick ?? t('pick.race_tag_pick'))
  const hasMore = nextMore.length + past.length > 0
  const fmt = (d) => d ? new Date(d).toLocaleDateString(i18n.language, { day: '2-digit', month: 'short' }) : ''

  const Row = ({ r }) => {
    const on = r.value === selected
    return (
      <Pressable
        onPress={() => { onSelect(r); setOpen(false) }}
        accessibilityRole="button"
        accessibilityState={{ selected: on }}
        style={({ pressed }) => [s.sheetRow, { borderBottomColor: tc.border }, on && { backgroundColor: tc.rosaSubtle }, pressed && { opacity: 0.7 }]}
      >
        <View style={{ flex: 1 }}>
          <Text style={[s.sheetName, { color: tc.text, fontWeight: on ? '700' : '500' }]} numberOfLines={2}>{r.label}</Text>
          <Text style={[s.sheetMeta, { color: tc.textMuted }]}>{fmt(r.date)} · {tagOf(r.status)}</Text>
        </View>
        {on && <MaterialCommunityIcons name="check" size={18} color={tc.rosaText} />}
      </Pressable>
    )
  }

  return (
    <>
      <View style={s.row} accessibilityRole="tablist" accessibilityLabel={t('pick.race_switcher_label')}>
        {pills.map((r) => {
          const active = r.value === selected
          return (
            <Pressable
              key={r.value}
              onPress={() => onSelect(r)}
              accessibilityRole="tab"
              accessibilityState={{ selected: active }}
              accessibilityLabel={`${r.label}, ${tagOf(r.status)}`}
              style={({ pressed }) => [
                s.pill,
                { backgroundColor: active ? tc.rosa : tc.bgCard2, borderColor: active ? tc.rosa : tc.border },
                pressed && { opacity: 0.75 },
              ]}
            >
              <Text style={[s.name, { color: active ? tc.brandText : tc.text }]} numberOfLines={2}>{r.label}</Text>
              <View style={s.tagRow}>
                {r.status === 'live' && <View style={[s.dot, { backgroundColor: active ? tc.brandText : tc.rosa }]} />}
                <Text style={[s.tag, { color: active ? tc.brandText : tc.textMuted }]}>{tagOf(r.status)}</Text>
              </View>
            </Pressable>
          )
        })}
        {hasMore && (
          <Pressable
            onPress={() => setOpen(true)}
            accessibilityRole="button"
            accessibilityLabel={t('pick.race_history')}
            style={({ pressed }) => [s.moreBtn, { backgroundColor: tc.bgCard2, borderColor: tc.border }, pressed && { opacity: 0.7 }]}
          >
            <MaterialCommunityIcons name="history" size={20} color={tc.textMuted} />
          </Pressable>
        )}
      </View>

      <Modal visible={open} transparent animationType="slide" onRequestClose={() => setOpen(false)}>
        <View style={s.overlay}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setOpen(false)} accessibilityRole="button" accessibilityLabel={t('pick.close')} />
          <View style={[s.sheet, { backgroundColor: tc.bgCard, paddingBottom: insets.bottom + 12 }]}>
            <View style={[s.sheetHeader, { borderBottomColor: tc.border }]}>
              <Text style={[s.sheetTitle, { color: tc.text }]}>{t('pick.race_history')}</Text>
              <Pressable onPress={() => setOpen(false)} hitSlop={6} style={[s.closeBtn, { backgroundColor: tc.bgCard2 }]} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
                <MaterialCommunityIcons name="close" size={18} color={tc.text} />
              </Pressable>
            </View>
            <ScrollView>
              {nextMore.length > 0 && (
                <>
                  <Text style={[s.group, { color: tc.textMuted }]}>{t('pick.race_group_next')}</Text>
                  {nextMore.map(r => <Row key={r.value} r={r} />)}
                </>
              )}
              {past.length > 0 && (
                <>
                  <Text style={[s.group, { color: tc.textMuted }]}>{t('pick.race_group_past')}</Text>
                  {past.map(r => <Row key={r.value} r={r} />)}
                </>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </>
  )
}

const s = StyleSheet.create({
  row: { flexDirection: 'row', gap: 8, paddingHorizontal: 12, paddingTop: 10, paddingBottom: 6 },
  pill: { flex: 1, minHeight: 56, borderRadius: 12, borderWidth: 1, paddingHorizontal: 10, paddingVertical: 6, justifyContent: 'center' },
  name: { fontFamily: fonts.condensedBold, fontSize: 14, lineHeight: 16 },
  tagRow: { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 2 },
  dot: { width: 6, height: 6, borderRadius: 3 },
  tag: { fontSize: 9, fontWeight: '700', letterSpacing: 1.2 },
  moreBtn: { width: 44, minHeight: 56, borderRadius: 12, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '75%' },
  sheetHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 0.5 },
  sheetTitle: { fontFamily: fonts.condensedBold, fontSize: 18 },
  closeBtn: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  group: { fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 1, paddingHorizontal: 16, paddingTop: 14, paddingBottom: 6 },
  sheetRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 10, minHeight: 56, borderBottomWidth: 0.5 },
  sheetName: { fontSize: 14 },
  sheetMeta: { fontSize: 11, marginTop: 2 },
})
